import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SecurityBadges from '../components/SecurityBadges';

const Index: React.FC = () => {
  // Define a reusable CTA button component for consistency
  const CTAButton = ({ to }: { to: string }) => (
    <Link 
      to={to} 
      className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 rounded-md text-white shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1 w-full max-w-xs md:max-w-md"
      style={{ 
        backgroundColor: '#023047',
        fontWeight: 600,
        fontSize: '20px',
        letterSpacing: '0.01em',
      }}
    >
      Jetzt Adresse finden
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 ml-2" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </Link>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-8 md:py-20 md:pb-0 px-4 container mx-auto" style={{ maxWidth: '1200px' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-16">
            <div className="md:w-1/2 text-center md:text-left">
              <h1 
                className="text-3xl sm:text-4xl font-bold mb-4 md:mb-6 text-center" 
                style={{ 
                  color: '#023047',
                  lineHeight: '1.5'
                }}
              >
                <div className="mb-3" style={{ whiteSpace: 'nowrap' }}>Schnell, sicher & digital:</div>
                <div className="underline decoration-2 underline-offset-4">Ihre Online-Adressauskunft ganz ohne Behördengang</div>
              </h1>
              
              <div className="hidden md:block mt-10 text-center">
                <CTAButton to="/personal-data" />
              </div>
              
              {/* Success Badge - Option 1: Solid Badge */}
              <div className="flex justify-center mt-8">
                <div 
                  className="flex items-center gap-2 px-4 py-2 rounded-full shadow-md"
                  style={{ 
                    backgroundColor: '#32936F',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white font-medium">Über 200 erfolgreiche Auskünfte in den letzten 30 Tagen</span>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 md:pl-6 order-2 md:order-none">
              <img
                src="/images/419d55fc-b22a-4333-affb-8991125e9217.png"
                alt="Beispiel eines Personalausweises mit Adresse"
                className="w-full max-w-[400px] md:max-w-none mx-auto md:mx-0 h-auto rounded-lg shadow-md"
              />
            </div>
          </div>
          
          <div className="mt-6 md:hidden text-center">
            <CTAButton to="/personal-data" />
          </div>
          
          <div className="mt-14 text-center hidden">
            <CTAButton to="/personal-data" />
          </div>
        </section>
        
        {/* Service Description - Full width to match the hero section */}
        <section className="py-8 md:py-16 px-4 container mx-auto" style={{ maxWidth: '1200px' }}>
          <div className="text-center max-w-4xl mx-auto">
            <p className="mb-4 md:mb-8 text-base md:text-2xl" style={{ 
              color: '#333',
              lineHeight: '1.4',
              fontWeight: 400,
            }}>
              <strong>Wir helfen Ihnen dabei, die aktuelle Anschrift einer Person</strong> in Deutschland zu finden – sei es aus privaten oder geschäftlichen Gründen.
            </p>
            
            <p className="mb-6 md:mb-12 text-base md:text-2xl" style={{ 
              color: '#333',
              lineHeight: '1.4',
              fontWeight: 400,
            }}>
              Über unsere sichere Online-Plattform können Sie bequem und rund um die Uhr eine <strong>Adressrecherche</strong> in Auftrag geben, ohne einen Behördengang machen zu müssen.
            </p>
            
            <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-10" style={{ color: '#023047' }}>
              Ermitteln Sie jetzt die aktuelle Meldeanschrift einer Person in Deutschland:
            </h2>
            
            <div className="mt-6 md:mt-10 mb-8 md:mb-16">
              <CTAButton to="/personal-data" />
            </div>
            
            <SecurityBadges />
          </div>
        </section>
        
        {/* Benefits Section - Redesigned with new color palette */}
        <section className="py-8 md:py-16 px-4 container mx-auto" style={{ maxWidth: '1200px' }}>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-14 text-prussian-blue">
            Warum AdressFinder.org die beste Wahl für Sie ist:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">Sofort-Ergebnisse</h3>
                <p className="text-dark-gray">Wir liefern die aktuelle Anschrift in den meisten Fällen unmittelbar.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">24/7 verfügbar</h3>
                <p className="text-dark-gray">Keine Behördengänge – Auftrag bequem online starten.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">Sichere Übermittlung</h3>
                <p className="text-dark-gray">Wir übertragen Ihre Daten ausschließlich über SSL-verschlüsselte Verbindungen.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 100 13.5 6.75 6.75 0 000-13.5zM2.25 10.5a8.25 8.25 0 1114.59 5.28l4.69 4.69a.75.75 0 11-1.06 1.06l-4.69-4.69A8.25 8.25 0 012.25 10.5z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">100% Transparenz</h3>
                <p className="text-dark-gray">Keine versteckten Kosten, kompetenter Support bei allen Fragen.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-.673-.05A3 3 0 0015 1.5h-1.5a3 3 0 00-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6zM13.5 3A1.5 1.5 0 0012 4.5h4.5A1.5 1.5 0 0015 3h-1.5z" clipRule="evenodd" />
                    <path fillRule="evenodd" d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V9.375zm9.586 4.594a.75.75 0 00-1.172-.872l-2.476 3.096-.908-.907a.75.75 0 00-1.06 1.06l1.5 1.5a.75.75 0 001.116-.062l3-3.75z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">Bewährter Service</h3>
                <p className="text-dark-gray">Über 200 erfolgreiche Auskünfte in den letzten 30 Tagen.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-medium-gray overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-prussian-blue"></div>
              <div className="absolute top-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500" style={{ backgroundColor: '#32936F' }}></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 bg-prussian-blue text-white w-16 h-16 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.75.75 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl mb-3 text-prussian-blue group-hover:text-celestial-blue transition-colors duration-300">Datenschutz</h3>
                <p className="text-dark-gray">Ihre persönlichen Daten werden nach DSGVO-Standards geschützt und vertraulich behandelt.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-8 md:py-16 px-4 container mx-auto" style={{ maxWidth: '1200px' }}>
          <div className="text-center">
            <h3 className="text-lg md:text-xl font-medium mb-4 md:mb-8" style={{ color: '#333' }}>Aktuelle Anschrift finden – schnell, sicher und direkt online:</h3>

            <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-10 text-prussian-blue">
              Ermitteln Sie jetzt die aktuelle Meldeanschrift einer Person in Deutschland:
            </h2>

            <div className="mt-6 md:mt-10">
              <CTAButton to="/personal-data" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
